import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import store from '../../store';
import merge from 'lodash/merge';
import {AuthenticationService} from '../../modules/authentication/services/authentication.service';
import { ApplicationConfigService } from '../../modules/services/application-config/application-config.service';
import { NotificationUtil } from '../util/notification.util';

export class BaseFactory {

    private axios = axios.create();

    protected requestConfig: AxiosRequestConfig;

    constructor(useAuthorization = true, useXBusCompany = true, BaseApi: string = null) {
        this.requestConfig = {};
        // this.requestConfig.baseURL = BaseApi || `${process.env.BASE_API}`;
        this.requestConfig.baseURL = BaseApi || `${process.env.VUE_APP_API_URL}`;
        this.requestConfig.timeout = 150000;

        this.axios.interceptors.response.use(this.onRequestSuccess, this.onRequestError);
        if (useAuthorization) {
            this.axios.interceptors.request.use(this.generateAuthorizationHeader);
        }
        if (useXBusCompany) {
            this.axios.interceptors.request.use(this.generateXBusCompanyHeader);
        }
    }

    protected post = async (url: string, body: any, config?: any) => {
        try {
            const result = await this.axios.post(url, body, this.getRequestConfigAssigned(config));
            return result.data;
        } catch (error) {
            throw error;
        }
    }

    protected put = async (url: string, body: any, config?: any) => {
        try {
            const result = await this.axios.put(url, body, this.getRequestConfigAssigned(config));
            return result.data;
        } catch (error) {
            throw error;
        }
    }

    protected patch = async (url: string, body: any, config?: any) => {
        try {
            const result = await this.axios.patch(url, body, this.getRequestConfigAssigned(config));
            return result.data;
        } catch (error) {
            throw error;
        }
    }

    protected get = async (url: string, config?: any) => {
        try {
            const result = await this.axios.get(url, this.getRequestConfigAssigned(config));
            return result.data;
        } catch (error) {
            throw error;
        }
    }

    protected getXls = async (url: string, config?: any) => {
        try {
            const xlsConfig = merge({}, config);
            xlsConfig.responseType = 'arraybuffer';
            if (!xlsConfig.headers) xlsConfig.headers = {};
            xlsConfig.headers['Accept'] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

            const result = await this.axios.get(url, this.getRequestConfigAssigned(xlsConfig));
            return result;

        } catch (error) {
            throw error;
        }
    }

    protected getPdf = async (url: string, config?: any) => {
        try {
            const pdfConfig = merge({}, config);
            pdfConfig.responseType = 'arraybuffer';
            if (!pdfConfig.headers) pdfConfig.headers = {};
            pdfConfig.headers['Accept'] = 'application/pdf';

            const result = await this.axios.get(url, this.getRequestConfigAssigned(pdfConfig));
            return result;

        } catch (error) {
            throw error;
        }
    }

    protected delete = async (url: string, config?: any) => {
        try {
            const result = await this.axios.delete(url, this.getRequestConfigAssigned(config));
            return result.data;
        } catch (error) {
            throw error;
        }
    }

    private onRequestSuccess = (response) => {
        return response;
    }

    private onRequestError = (error) => {
        if (error && error.response && error.response.status) {
            if (error.response.status === 401 || error.response.status === 403) {
                NotificationUtil.warning('Sua sessão expirou ou você não possui permissão de acesso.');
                AuthenticationService.logout(ApplicationConfigService.urlSuite);
            }
        }
        return Promise.reject(error);
    }

    private remakeRequest = async (config: AxiosRequestConfig) => {
        try {
            const newConfig = Object.assign({}, config);
            const result = await axios.request(newConfig);
            return result;
        } catch (error) {
            throw error;
        }
    }

    private generateXBusCompanyHeader = (request) => {
        const activeWorkspace = store.getters['auth/activeWorkspace'];
        if (activeWorkspace && activeWorkspace.company) {
            request.headers['X-BUS-COMPANY'] = activeWorkspace.company;
        }

        return request;
    }

    private generateAuthorizationHeader = (request) => {
        request.headers['Authorization'] = 'Bearer ' + localStorage.getItem(AuthenticationService.TOKEN_KEY);
        return request;
    }

    private getRequestConfigAssigned(config?: AxiosRequestConfig) {
        const configInitial = merge({}, this.requestConfig);
        return merge(configInitial, config) as AxiosRequestConfig;
    }

}
